import { FaChrome } from "react-icons/fa";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Globe,
  RefreshCcw,
  ExternalLink,
  CheckCircle2,
  Clock,
  AlertTriangle,
  HelpCircle,
  CircleSlash,
} from "lucide-react";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  getPluginStoreVersions,
  StoreVersionInfo,
} from "@/services/pluginStoreVersionsApi";

// Maps the backend review_state code → French label, colour and icon.
const REVIEW_STATE: Record<
  string,
  { label: string; className: string; icon: typeof Clock }
> = {
  in_review: {
    label: "En cours de vérification",
    className: "border-transparent bg-amber-100 text-amber-800",
    icon: Clock,
  },
  processing: {
    label: "En traitement",
    className: "border-transparent bg-amber-100 text-amber-800",
    icon: Clock,
  },
  none: {
    label: "À jour",
    className: "border-transparent bg-green-100 text-green-800",
    icon: CheckCircle2,
  },
  no_data: {
    label: "Aucune publication enregistrée",
    className: "border-transparent bg-slate-100 text-slate-600",
    icon: CircleSlash,
  },
  unknown: {
    label: "Indéterminé",
    className: "border-transparent bg-slate-100 text-slate-600",
    icon: HelpCircle,
  },
  not_configured: {
    label: "Identifiants non configurés",
    className: "border-transparent bg-slate-100 text-slate-600",
    icon: CircleSlash,
  },
  unsupported: {
    label: "Non exposé par l'API du store",
    className: "border-transparent bg-slate-100 text-slate-600",
    icon: CircleSlash,
  },
  error: {
    label: "Erreur",
    className: "border-transparent bg-red-100 text-red-800",
    icon: AlertTriangle,
  },
};

function StoreIcon({ store }: { store: string }) {
  const Icon = store === "chrome" ? FaChrome : Globe;
  return <Icon className="h-5 w-5 text-muted-foreground" />;
}

function ReviewBadge({ info }: { info: StoreVersionInfo }) {
  const cfg = REVIEW_STATE[info.review_state] ?? REVIEW_STATE.unknown;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={cfg.className}>
      <Icon className="mr-1 h-3 w-3" />
      {cfg.label}
    </Badge>
  );
}

function StoreCard({ info }: { info: StoreVersionInfo }) {
  const reviewing =
    info.review_state === "in_review" || info.review_state === "processing";

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <StoreIcon store={info.store} />
          {info.label}
        </CardTitle>
        {info.store_url && (
          <a
            href={info.store_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            Voir la fiche <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Published version */}
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Version publiée
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-3xl font-bold tabular-nums">
              {info.published_version ?? "—"}
            </span>
            {info.published_state === "published" && (
              <Badge
                variant="outline"
                className="border-transparent bg-green-100 text-green-800"
              >
                <CheckCircle2 className="mr-1 h-3 w-3" />
                Publié
              </Badge>
            )}
          </div>
        </div>

        {/* Under review */}
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            En cours de vérification
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <ReviewBadge info={info} />
            {reviewing && (
              <span className="text-sm font-medium tabular-nums">
                {info.review_version ?? "version inconnue"}
              </span>
            )}
          </div>
          {info.submitted_at && (
            <div className="mt-1 text-xs text-muted-foreground">
              Dernière soumission&nbsp;:{" "}
              {new Date(info.submitted_at).toLocaleString("fr-FR")}
              {info.last_publish_status ? ` · ${info.last_publish_status}` : ""}
            </div>
          )}
        </div>

        {/* Identity + errors */}
        <div className="space-y-1 border-t pt-3 text-xs text-muted-foreground">
          {info.extension_id && (
            <div>
              ID&nbsp;:&nbsp;
              <span className="font-mono">{info.extension_id}</span>
            </div>
          )}
          {info.errors.length > 0 && (
            <div className="text-red-600">
              {info.errors.map((e, i) => (
                <div key={i}>⚠ {e}</div>
              ))}
            </div>
          )}
        </div>

        {/* Raw payloads — ground truth for debugging */}
        {Object.keys(info.raw).length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Détails techniques (réponses brutes)
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-muted p-3 text-[11px] leading-snug">
              {JSON.stringify(info.raw, null, 2)}
            </pre>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

export default function PluginStoreVersionsPage() {
  const navigate = useNavigate();
  const [stores, setStores] = useState<StoreVersionInfo[]>([]);
  const [checkedAt, setCheckedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPluginStoreVersions(navigate);
      setStores(data.stores);
      setCheckedAt(data.checked_at);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex flex-1 items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Versions stores</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <div className="ml-auto flex items-center gap-3">
              {checkedAt && (
                <span className="hidden text-xs text-muted-foreground sm:inline">
                  Vérifié le {new Date(checkedAt).toLocaleString("fr-FR")}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={refresh}
                disabled={loading}
              >
                <RefreshCcw
                  className={`mr-1 h-3 w-3 ${loading ? "animate-spin" : ""}`}
                />
                Rafraîchir
              </Button>
            </div>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          <p className="text-sm text-muted-foreground">
            État de publication de l'extension RecrutAuto sur les stores. La
            version publiée est lue via les endpoints publics des stores ; l'état
            « en cours de vérification » utilise les identifiants de publication
            (mêmes secrets que les pipelines Tekton). Selon le store, l'API peut
            ne pas exposer la version exacte en cours de revue.
          </p>

          {error && <div className="text-sm text-red-600">{error}</div>}

          {loading && stores.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Chargement...
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {stores.map((info) => (
                <StoreCard key={info.store} info={info} />
              ))}
            </div>
          )}
        </div>
      </SidebarInset>
    </>
  );
}
